/**
 * Cell renderers extracted from ImagingWorklistStudyRow to reduce cognitive complexity.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Icons, Tooltip, TooltipTrigger, TooltipContent } from '@ohif/ui-next';
import classnames from 'classnames';
import {
  formatPriorityBadgeText,
  formatPriorityDisplay,
  formatReportStatusLabel,
  formatImagingModalityDisplay,
  modalityTokens,
  reportStatusClass,
  resolveAiResultBadge,
  aiResultClass,
} from './imagingWorklistTableUtils';
import { CriticalCaseIcon } from './CriticalCaseIcon';
import { AiSparkleIcon } from './AiSparkleIcon';

type StudyNameFields = {
  patientName?: string;
  criticalReason?: string;
};

export function ImagingWorklistPatientNameCell({
  study,
  isCritical,
}: Readonly<{
  study: StudyNameFields;
  isCritical: boolean;
}>) {
  return (
    <td className="max-w-[180px] px-3 py-3">
      <div className="flex min-w-0 items-center gap-2">
        {isCritical ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0 items-center justify-center">
                <CriticalCaseIcon className="shrink-0" />
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="max-w-xs"
            >
              <div className="space-y-1 text-left">
                <div className="font-semibold">Critical case</div>
                <div className="text-sm text-slate-600">
                  {study.criticalReason?.trim()
                    ? study.criticalReason
                    : 'This case has been marked as critical.'}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
        <span className="truncate font-medium">{study.patientName || '—'}</span>
      </div>
    </td>
  );
}

export function ImagingWorklistModalityCell({ modalities }: Readonly<{ modalities?: string }>) {
  const tokens = modalityTokens(modalities);
  return (
    <td className="imaging-worklist-modality">
      <div className="imaging-worklist-modality__inner">
        {tokens.map(modalityToken => (
          <span
            key={modalityToken}
            className="imaging-worklist-modality-badge"
          >
            {formatImagingModalityDisplay(modalityToken)}
          </span>
        ))}
        {tokens.length === 0 ? <span className="imaging-worklist-modality__empty">—</span> : null}
      </div>
    </td>
  );
}

export function ImagingWorklistPriorityCell({
  priorityRaw,
  priorityIsUrgentPill,
  worklistBoostRule,
  studyPriority,
}: Readonly<{
  priorityRaw: string;
  priorityIsUrgentPill: boolean;
  worklistBoostRule: string;
  studyPriority?: string;
}>) {
  return (
    <td
      className="whitespace-nowrap px-3 py-3 text-center align-middle"
      title={worklistBoostRule ? `Worklist boost: ${worklistBoostRule}` : undefined}
    >
      {priorityRaw ? (
        <div className="imaging-worklist-priority-cell">
          <span
            className={classnames(
              'imaging-worklist-priority-pill',
              priorityIsUrgentPill
                ? 'imaging-worklist-priority-pill--urgent'
                : 'imaging-worklist-priority-pill--muted'
            )}
          >
            {priorityIsUrgentPill
              ? formatPriorityBadgeText(priorityRaw)
              : formatPriorityDisplay(studyPriority)}
          </span>
          {worklistBoostRule ? (
            <span
              className="imaging-worklist-priority-boost"
              aria-hidden="true"
            >
              ↑
            </span>
          ) : null}
        </div>
      ) : (
        <span className="inline-block text-slate-400">—</span>
      )}
    </td>
  );
}

export function ImagingWorklistReportStatusCell({
  reportStatus,
}: Readonly<{ reportStatus?: string }>) {
  if (!reportStatus?.trim()) {
    return (
      <td className="whitespace-nowrap px-3 py-3 text-center align-middle">
        <span className="inline-block text-slate-400">—</span>
      </td>
    );
  }
  return (
    <td className="whitespace-nowrap px-3 py-3 text-center align-middle">
      <span className={classnames('imaging-worklist-report-pill', reportStatusClass(reportStatus))}>
        {formatReportStatusLabel(reportStatus)}
      </span>
    </td>
  );
}

export function ImagingWorklistActionCell({ launchHref }: Readonly<{ launchHref: string | null }>) {
  return (
    <td
      className="imaging-worklist-table__action-col"
      onClick={e => e.stopPropagation()}
    >
      <div className="imaging-worklist-action-col__inner">
        {launchHref ? (
          <Link
            to={launchHref}
            className="imaging-worklist-table__action-link"
          >
            <Icons.EyeVisible className="imaging-worklist-table__action-icon" />
            View
          </Link>
        ) : (
          <span className="inline-block text-xs text-slate-400">—</span>
        )}
      </div>
    </td>
  );
}

export function ImagingWorklistAiResultCell({
  aiStatus,
  aiClassification,
  aiModelVersion,
}: Readonly<{
  aiStatus?: string;
  aiClassification?: string;
  aiModelVersion?: string;
}>) {
  const aiBadge = resolveAiResultBadge({
    aiStatus,
    aiClassification,
    aiModelVersion,
  });

  return (
    <td className="whitespace-nowrap px-3 py-3 text-center align-middle">
      {aiBadge ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={classnames('imaging-worklist-ai-pill', aiResultClass(aiBadge.variant))}
            >
              {aiBadge.showSparkle ? (
                <AiSparkleIcon className="imaging-worklist-ai-pill__icon" />
              ) : null}
              {aiBadge.label}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-xs whitespace-pre-line text-left text-xs"
          >
            {aiBadge.tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className="inline-block text-slate-400">—</span>
      )}
    </td>
  );
}
